import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationsContext = createContext(null);
export const useNotifications = () => useContext(NotificationsContext);

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const handlers = useRef(new Set());
  const refreshTimer = useRef(null);

  // pages subscribe to raw chat events
  const addHandler = useCallback((fn) => {
    handlers.current.add(fn);
    return () => handlers.current.delete(fn);
  }, []);

  const refreshUnread = useCallback(async () => {
    try {
      const r = await api.get("/chat/conversations");
      setUnread(r.data.reduce((a, c) => a + (c.unread || 0), 0));
    } catch { /* noop */ }
  }, []);

  const debouncedRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(refreshUnread, 400);
  }, [refreshUnread]);

  // capture install prompt for "Add to home screen"
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installEvent) return false;
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    setInstallEvent(null);
    return outcome === "accepted";
  }, [installEvent]);

  // app-wide SSE stream (only when logged in)
  useEffect(() => {
    if (!user || !user.id) return;
    refreshUnread();
    // periodic fallback so the bell/unread still update if SSE is buffered behind a CDN/proxy
    const poll = setInterval(refreshUnread, 15000);
    const es = new EventSource(`${BACKEND}/api/chat/stream`, { withCredentials: true });
    es.onopen = () => setConnected(true);
    es.onmessage = (e) => {
      let ev;
      try { ev = JSON.parse(e.data); } catch { return; }
      if (ev.type === "ready") { setConnected(true); return; }
      handlers.current.forEach((fn) => { try { fn(ev); } catch { /* noop */ } });
      if (ev.type === "message" && ev.message && ev.message.sender_id !== user.id) {
        debouncedRefresh();
        if (!window.location.pathname.startsWith("/mates")) {
          const m = ev.message;
          const body = m.text || (m.context?.type === "poll" ? "started a poll"
            : m.context?.type === "slip" ? "built a bet slip" : "shared something");
          toast(`${m.sender_name}`, {
            description: body.slice(0, 80),
            action: { label: "Open", onClick: () => navigate("/mates") },
          });
        }
      }
    };
    es.onerror = () => setConnected(false);
    return () => { clearInterval(poll); es.close(); };
  }, [user, refreshUnread, debouncedRefresh, navigate]);

  return (
    <NotificationsContext.Provider value={{
      unread, connected, addHandler, refreshUnread,
      canInstall: !!installEvent, promptInstall,
    }}>
      {children}
    </NotificationsContext.Provider>
  );
}
